import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Plus, Search, Filter, Package, Tag, Edit2, Trash2, X, ShoppingBag, AlertCircle, Loader2, ImagePlus, Camera, ChevronLeft, ChevronRight, User as UserIcon, Calendar, Eye, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { marketplaceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const CATEGORIES = [
    { value: 'all', labelKey: 'marketplace.allCategories' },
    { value: 'krampon', labelKey: 'marketplace.categories.krampon' },
    { value: 'kask', labelKey: 'marketplace.categories.kask' },
    { value: 'shoulderpad', labelKey: 'marketplace.categories.shoulderpad' },
    { value: 'eldiven', labelKey: 'marketplace.categories.eldiven' },
    { value: 'dislik', labelKey: 'marketplace.categories.dislik' },
    { value: 'aksesuar', labelKey: 'marketplace.categories.aksesuar' },
    { value: 'giyim', labelKey: 'marketplace.categories.giyim' },
    { value: 'diger', labelKey: 'marketplace.categories.diger' },
];

const MAX_PHOTOS = 4;

const Market = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState('browse'); // browse | my
    const [listings, setListings] = useState([]);
    const [myListings, setMyListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limitInfo, setLimitInfo] = useState(null);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showNoPermissionModal, setShowNoPermissionModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);
    const [selectedImageIdx, setSelectedImageIdx] = useState(0);

    const [form, setForm] = useState({
        title: '',
        description: '',
        price: '',
        currency: 'TRY',
        category: 'krampon',
        image_urls: [],
    });

    const fetchListings = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (category !== 'all') params.category = category;
            if (search) params.search = search;
            const response = await marketplaceAPI.getListings(params);
            setListings(response.data.listings || []);
        } catch (error) {
            console.error('Failed to load listings:', error);
        } finally {
            setLoading(false);
        }
    }, [category, search]);

    const fetchMyListings = useCallback(async () => {
        try {
            const response = await marketplaceAPI.getMyListings();
            setMyListings(response.data || []);
        } catch (error) {
            console.error('Failed to load my listings:', error);
        }
    }, []);

    const fetchLimitInfo = useCallback(async () => {
        try {
            const response = await marketplaceAPI.getMyLimit();
            setLimitInfo(response.data);
        } catch (error) {
            console.error('Failed to load limit info:', error);
        }
    }, []);

    useEffect(() => {
        fetchListings();
        if (user) {
            fetchLimitInfo();
        }
    }, [fetchListings, fetchLimitInfo, user]);

    useEffect(() => {
        if (activeTab === 'my' && user) {
            fetchMyListings();
        }
    }, [activeTab, fetchMyListings, user]);

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const remaining = MAX_PHOTOS - form.image_urls.length;
        const toUpload = files.slice(0, remaining);

        if (files.length > remaining) {
            toast({
                variant: 'destructive',
                title: t('marketplace.form.maxPhotos', `En fazla ${MAX_PHOTOS} fotoğraf yükleyebilirsiniz`),
            });
        }

        setUploading(true);
        const newUrls = [...form.image_urls];

        for (const file of toUpload) {
            try {
                const response = await marketplaceAPI.uploadImage(file);
                newUrls.push(response.data.image_url);
            } catch (error) {
                console.error('Image upload failed:', error);
                toast({
                    variant: 'destructive',
                    title: t('common.error'),
                    description: t('marketplace.form.uploadFailed', 'Fotoğraf yüklenemedi'),
                });
            }
        }

        setForm(prev => ({ ...prev, image_urls: newUrls }));
        setUploading(false);
        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeImage = (index) => {
        setForm(prev => ({
            ...prev,
            image_urls: prev.image_urls.filter((_, i) => i !== index),
        }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.price) return;

        setCreating(true);
        try {
            await marketplaceAPI.createListing({
                title: form.title.trim(),
                description: form.description.trim() || null,
                price: parseFloat(form.price),
                currency: form.currency,
                category: form.category,
                image_urls: form.image_urls.filter(Boolean),
            });
            toast({ title: t('common.success'), description: t('marketplace.createSuccess') });
            setShowCreateModal(false);
            setForm({ title: '', description: '', price: '', currency: 'TRY', category: 'krampon', image_urls: [] });
            fetchListings();
            fetchMyListings();
            fetchLimitInfo();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail?.code === 'LISTING_LIMIT_REACHED') {
                toast({
                    variant: 'destructive',
                    title: t('marketplace.limitReachedTitle'),
                    description: t('marketplace.limitReachedDesc'),
                });
            } else {
                toast({ variant: 'destructive', title: t('common.error'), description: t('marketplace.createError') });
            }
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('marketplace.deleteConfirm'))) return;
        try {
            await marketplaceAPI.deleteListing(id);
            toast({ title: t('common.success'), description: t('marketplace.deleteSuccess') });
            fetchListings();
            fetchMyListings();
            fetchLimitInfo();
        } catch (error) {
            toast({ variant: 'destructive', title: t('common.error'), description: t('marketplace.deleteError') });
        }
    };

    const handleMarkSold = async (id) => {
        try {
            await marketplaceAPI.updateListing(id, { status: 'sold' });
            toast({ title: t('common.success'), description: t('marketplace.markedSold') });
            fetchListings();
            fetchMyListings();
            fetchLimitInfo();
        } catch (error) {
            toast({ variant: 'destructive', title: t('common.error') });
        }
    };

    const categoryLabel = (cat) => {
        // Handle cases where cat might be Enum string like 'ListingCategory.krampon' or null
        const normalizedCat = cat ? String(cat).split('.').pop().toLowerCase() : 'diger';
        const key = `marketplace.categories.${normalizedCat}`;
        return t(key) || normalizedCat;
    };

    const statusBadge = (status) => {
        const colors = {
            active: 'bg-green-500/20 text-green-400 border-green-500/30',
            sold: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
            archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        };
        return colors[status] || colors.active;
    };

    const displayListings = activeTab === 'my' ? myListings : listings;

    return (
        <div className="p-4 md:p-8 bg-[#0a0a0b] min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <Store className="w-8 h-8 text-emerald-500" />
                            {t('marketplace.title')}
                        </h1>
                        <p className="text-gray-400 mt-1">{t('marketplace.subtitle')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {limitInfo && limitInfo.allowed_limit > 0 && (
                            <div className="hidden sm:flex items-center gap-2 bg-[#1a1a1b] border border-gray-800 rounded-lg px-3 py-2">
                                <Package className="w-4 h-4 text-emerald-500" />
                                <span className="text-sm text-gray-400">
                                    {t('marketplace.remaining')}: <span className="text-white font-bold">{limitInfo.remaining}</span>/{limitInfo.allowed_limit}
                                </span>
                            </div>
                        )}
                        {user ? (
                            <button
                                onClick={() => {
                                    if (limitInfo && limitInfo.remaining <= 0) {
                                        setShowNoPermissionModal(true);
                                    } else {
                                        setShowCreateModal(true);
                                    }
                                }}
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                {t('marketplace.newListing')}
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/login')}
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
                            >
                                {t('marketplace.createListing', 'İlan Vermek İçin Giriş Yap')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs + Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                <div className="flex bg-[#1a1a1b] border border-gray-800 rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab('browse')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'browse' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        <ShoppingBag className="w-4 h-4 inline mr-2" />
                        {t('marketplace.browse')}
                    </button>
                    {user && (
                        <button
                            onClick={() => setActiveTab('my')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'my' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            <Tag className="w-4 h-4 inline mr-2" />
                            {t('marketplace.myListings')}
                        </button>
                    )}
                </div>

                {activeTab === 'browse' && (
                    <div className="flex flex-1 gap-3">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('marketplace.searchPlaceholder')}
                                className="w-full bg-[#1a1a1b] border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                            />
                        </div>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="bg-[#1a1a1b] border border-gray-800 text-gray-300 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                        >
                            {CATEGORIES.map(c => (
                                <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Listings */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
            ) : displayListings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <Store className="w-16 h-16 text-gray-700 mb-4" />
                    <p className="text-gray-500 text-lg">{t('marketplace.noListings')}</p>
                    {activeTab === 'browse' && (
                        <p className="text-gray-600 text-sm mt-1">{t('marketplace.noListingsDesc')}</p>
                    )}
                </div>
            ) : (
                <>
                    {/* Partner Sellers Section - only in browse tab */}
                    {activeTab === 'browse' && (() => {
                        const sellerListings = displayListings.filter(l => l.is_partner);
                        if (sellerListings.length === 0) return null;
                        return (
                            <div className="mb-8">
                                <div className="flex items-center gap-2 mb-4">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" fill="#1DA1F2" />
                                        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <h3 className="text-white font-semibold text-lg">Partner Satıcılar</h3>
                                </div>
                                <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                                    {sellerListings.map((listing) => (
                                        <div
                                            key={listing.id}
                                            onClick={() => {
                                                setSelectedListing(listing);
                                                setSelectedImageIdx(0);
                                                marketplaceAPI.recordView(listing.id).catch(() => { });
                                            }}
                                            className="flex-shrink-0 w-64 bg-[#1a1a1b] border border-blue-500/30 rounded-xl overflow-hidden hover:border-blue-400/50 transition-colors cursor-pointer snap-start group"
                                        >
                                            {/* Image */}
                                            <div className="h-40 bg-black flex items-center justify-center relative group-hover:opacity-95 transition-opacity">
                                                {listing.image_urls && listing.image_urls.length > 0 ? (
                                                    <img src={listing.image_urls[0]} alt={listing.title} className="w-full h-full object-contain" />
                                                ) : (
                                                    <Package className="w-10 h-10 text-gray-700" />
                                                )}
                                                {/* Username Badge */}
                                                {listing.owner_username && (
                                                    <span className="absolute top-2 left-2 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-black/70 text-white backdrop-blur-md flex items-center gap-1.5 shadow-xl border border-white/10 z-10">
                                                        @{listing.owner_username}
                                                        {listing.is_partner && (
                                                            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                                                                <circle cx="12" cy="12" r="10" fill="#1DA1F2" />
                                                                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                )}
                                                {/* Hidden from here: Photo count and category */}
                                            </div>
                                            {/* Content */}
                                            <div className="p-3 flex flex-col gap-1.5">
                                                <div className="flex items-center justify-between text-[11px] font-medium text-gray-400 h-4">
                                                    <span className="uppercase truncate pr-2">{categoryLabel(listing.category)}</span>
                                                    {listing.image_urls && listing.image_urls.length > 1 && (
                                                        <span className="flex items-center gap-1 flex-shrink-0">
                                                            <Camera className="w-3 h-3" /> {listing.image_urls.length}
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="text-white font-semibold text-sm truncate">{listing.title}</h3>
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-emerald-400 font-bold">{listing.price.toLocaleString('tr-TR')} {listing.currency}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Regular Listings Grid */}
                    {(() => {
                        const regularListings = activeTab === 'browse'
                            ? displayListings.filter(l => !l.is_partner)
                            : displayListings;
                        if (regularListings.length === 0 && activeTab === 'browse' && displayListings.some(l => l.is_partner)) return null;
                        return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {regularListings.map((listing) => (
                                    <div
                                        key={listing.id}
                                        onClick={() => {
                                            setSelectedListing(listing);
                                            setSelectedImageIdx(0);
                                            // Record view (fire and forget)
                                            if (activeTab === 'browse') {
                                                marketplaceAPI.recordView(listing.id).catch(() => { });
                                            }
                                        }}
                                        className="bg-[#1a1a1b] border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors group cursor-pointer"
                                    >
                                        {/* Image */}
                                        <div className="h-44 bg-black flex items-center justify-center relative group-hover:opacity-95 transition-opacity">
                                            {listing.image_urls && listing.image_urls.length > 0 ? (
                                                <img src={listing.image_urls[0]} alt={listing.title} className="w-full h-full object-contain" />
                                            ) : (
                                                <Package className="w-12 h-12 text-gray-700" />
                                            )}
                                            {/* Username Badge */}
                                            {listing.owner_username && activeTab === 'browse' && (
                                                <span className="absolute top-2 left-2 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-black/70 text-white backdrop-blur-md flex items-center gap-1.5 shadow-xl border border-white/10 z-10">
                                                    @{listing.owner_username}
                                                    {listing.is_partner && (
                                                        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                                                            <circle cx="12" cy="12" r="10" fill="#1DA1F2" />
                                                            <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </span>
                                            )}
                                            {/* Hidden from here: category and camera. (Status badge kept on image for 'my' tab) */}
                                            {activeTab === 'my' && (
                                                <span className={`absolute top-2 right-2 text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${statusBadge(listing.status)} z-10`}>
                                                    {t(`marketplace.status.${listing.status}`)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="p-4 flex flex-col gap-1.5">
                                            <div className="flex items-center justify-between text-[11px] font-medium text-gray-400 h-4">
                                                <span className="uppercase truncate pr-2">{categoryLabel(listing.category)}</span>
                                                {listing.image_urls && listing.image_urls.length > 1 && (
                                                    <span className="flex items-center gap-1 flex-shrink-0">
                                                        <Camera className="w-3 h-3" /> {listing.image_urls.length}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-white font-semibold truncate leading-tight mt-0.5">{listing.title}</h3>
                                            {listing.description && (
                                                <p className="text-gray-500 text-sm mt-0.5 line-clamp-2">{listing.description}</p>
                                            )}
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-emerald-400 font-bold text-lg">
                                                    {listing.price.toLocaleString('tr-TR')} {listing.currency}
                                                </span>
                                                <div className="flex items-center gap-3">
                                                    {activeTab === 'my' && (
                                                        <span className="flex items-center gap-1 text-gray-500 text-xs">
                                                            <Eye className="w-3.5 h-3.5" />
                                                            {listing.view_count || 0}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {activeTab === 'my' && listing.status === 'active' && (
                                                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-800">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleMarkSold(listing.id); }}
                                                        className="flex-1 text-xs bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg py-2 transition-colors"
                                                    >
                                                        {t('marketplace.markSold')}
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(listing.id); }}
                                                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </>
            )}

            {/* Create Listing Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-[#1a1a1b] border border-gray-800 rounded-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={() => { setShowCreateModal(false); setForm({ title: '', description: '', price: '', currency: 'TRY', category: 'equipment', image_urls: [] }); }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-emerald-500" />
                            {t('marketplace.createTitle')}
                        </h3>
                        {limitInfo && (
                            <p className="text-sm text-gray-500 mb-6">
                                {t('marketplace.remaining')}: <span className="text-emerald-400 font-bold">{limitInfo.remaining}</span>
                            </p>
                        )}

                        <form onSubmit={handleCreate} className="space-y-4">
                            {/* Photo Upload Section */}
                            <div>
                                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    {t('marketplace.form.photos', 'Fotoğraflar')} ({form.image_urls.length}/{MAX_PHOTOS})
                                </label>
                                <div className="mt-2 grid grid-cols-4 gap-2">
                                    {/* Existing photo previews */}
                                    {form.image_urls.map((url, index) => (
                                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-700 group">
                                            <img src={url} alt="" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                            {index === 0 && (
                                                <span className="absolute bottom-1 left-1 text-[8px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">
                                                    {t('marketplace.form.cover', 'Kapak')}
                                                </span>
                                            )}
                                        </div>
                                    ))}

                                    {/* Add photo button */}
                                    {form.image_urls.length < MAX_PHOTOS && (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="aspect-square rounded-lg border-2 border-dashed border-gray-700 hover:border-emerald-500/50 flex flex-col items-center justify-center text-gray-500 hover:text-emerald-500 transition-colors cursor-pointer disabled:opacity-50"
                                        >
                                            {uploading ? (
                                                <Loader2 className="w-6 h-6 animate-spin" />
                                            ) : (
                                                <>
                                                    <ImagePlus className="w-6 h-6" />
                                                    <span className="text-[10px] mt-1">{t('marketplace.form.addPhoto', 'Ekle')}</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    multiple
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('marketplace.form.title')}</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder={t('marketplace.form.titlePlaceholder')}
                                    className="w-full mt-1 bg-[#0a0a0b] border border-gray-700 rounded-lg p-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                                    required
                                />
                            </div>

                            <div>
                                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('marketplace.form.description')}</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder={t('marketplace.form.descriptionPlaceholder')}
                                    rows={3}
                                    className="w-full mt-1 bg-[#0a0a0b] border border-gray-700 rounded-lg p-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('marketplace.form.price')}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.price}
                                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                                        placeholder="0.00"
                                        className="w-full mt-1 bg-[#0a0a0b] border border-gray-700 rounded-lg p-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('marketplace.form.category')}</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        className="w-full mt-1 bg-[#0a0a0b] border border-gray-700 rounded-lg p-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                                    >
                                        {CATEGORIES.filter(c => c.value !== 'all').map(c => (
                                            <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setShowCreateModal(false); setForm({ title: '', description: '', price: '', currency: 'TRY', category: 'krampon', image_urls: [] }); }}
                                    className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-3 rounded-lg transition-colors"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating || uploading || !form.title.trim() || !form.price}
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {creating ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Plus className="w-4 h-4" />
                                            {t('marketplace.publish')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* No Permission / Out of Slots Modal */}
            {showNoPermissionModal && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-[#1a1a1b] border border-gray-800 rounded-xl p-8 w-full max-w-md relative text-center">
                        <button
                            onClick={() => setShowNoPermissionModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
                            <Store className="w-8 h-8 text-emerald-500" />
                        </div>

                        <h3 className="text-xl font-bold text-white mb-2">
                            {t('marketplace.noPermission')}
                        </h3>
                        <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                            Pazaryerinde ilan paylaşabilmek için ilan yayınlama limitiniz bitmiş veya ilan oluşturma yetkiniz bulunmamaktadır. Ek ilan hakkı veya pazaryeri yetkisi talep etmek için takım yöneticinizle ya da Snapp ekibi ile iletişime geçebilirsiniz.
                        </p>

                        <button
                            onClick={() => setShowNoPermissionModal(false)}
                            className="w-full bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 rounded-lg transition-colors"
                        >
                            {t('common.close')}
                        </button>
                    </div>
                </div>
            )}

            {/* Listing Detail Modal */}
            {selectedListing && (
                <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setSelectedListing(null)}>
                    <div className="bg-[#1a1a1b] border border-gray-800 rounded-xl w-full max-w-2xl relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setSelectedListing(null)}
                            className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Image Gallery */}
                        {selectedListing.image_urls && selectedListing.image_urls.length > 0 ? (
                            <div>
                                {/* Main Image */}
                                <div className="relative h-72 sm:h-96 bg-gray-900">
                                    <img
                                        src={selectedListing.image_urls[selectedImageIdx]}
                                        alt={selectedListing.title}
                                        className="w-full h-full object-contain"
                                    />
                                    {/* Navigation Arrows */}
                                    {selectedListing.image_urls.length > 1 && (
                                        <>
                                            <button
                                                onClick={() => setSelectedImageIdx(prev => prev === 0 ? selectedListing.image_urls.length - 1 : prev - 1)}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                                            >
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => setSelectedImageIdx(prev => prev === selectedListing.image_urls.length - 1 ? 0 : prev + 1)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                                            >
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                            {/* Image counter */}
                                            <span className="absolute bottom-3 right-3 text-xs bg-black/60 text-white px-2 py-1 rounded-full backdrop-blur-sm">
                                                {selectedImageIdx + 1} / {selectedListing.image_urls.length}
                                            </span>
                                        </>
                                    )}
                                </div>

                                {/* Thumbnail Strip */}
                                {selectedListing.image_urls.length > 1 && (
                                    <div className="flex gap-2 p-3 bg-[#111]">
                                        {selectedListing.image_urls.map((url, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setSelectedImageIdx(idx)}
                                                className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors flex-shrink-0 ${idx === selectedImageIdx ? 'border-emerald-500' : 'border-transparent hover:border-gray-600'}`}
                                            >
                                                <img src={url} alt="" className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="h-48 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                <Package className="w-16 h-16 text-gray-700" />
                            </div>
                        )}

                        {/* Details */}
                        <div className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <h2 className="text-xl font-bold text-white">{selectedListing.title}</h2>
                                    <div className="flex items-center gap-3 mt-2">
                                        <span className="text-[11px] font-medium uppercase px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                            {categoryLabel(selectedListing.category)}
                                        </span>
                                        {selectedListing.status && selectedListing.status !== 'active' && (
                                            <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-full border ${statusBadge(selectedListing.status)}`}>
                                                {t(`marketplace.status.${selectedListing.status}`)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-emerald-400">
                                        {selectedListing.price?.toLocaleString('tr-TR')}
                                    </span>
                                    <span className="text-emerald-400 text-lg ml-1">{selectedListing.currency}</span>
                                </div>
                            </div>

                            {selectedListing.description && (
                                <p className="text-gray-400 mt-4 text-sm leading-relaxed whitespace-pre-wrap">
                                    {selectedListing.description}
                                </p>
                            )}

                            {/* Actions & Seller Info */}
                            <div className="mt-5 pt-4 border-t border-gray-800 flex items-center justify-between">
                                {selectedListing.owner_username && (
                                    <div className="flex items-center gap-2">
                                        {selectedListing.owner_avatar_url ? (
                                            <img src={selectedListing.owner_avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                                                <UserIcon className="w-4 h-4 text-gray-500" />
                                            </div>
                                        )}
                                        <span className="text-sm text-gray-300">@{selectedListing.owner_username}</span>
                                        {selectedListing.is_partner && (
                                            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" fill="#1DA1F2" />
                                                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                )}
                                
                                <div className="flex items-center gap-4">
                                    {selectedListing.created_at && (
                                        <div className="flex items-center gap-1.5 text-gray-500 text-xs hidden sm:flex">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {new Date(selectedListing.created_at).toLocaleDateString('tr-TR')}
                                        </div>
                                    )}
                                    
                                    {/* Message Seller Button */}
                                    {selectedListing.owner_id !== user?.id && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!user) {
                                                    navigate('/login');
                                                    return;
                                                }
                                                navigate(`/messages/${selectedListing.owner_id}`, {
                                                    state: {
                                                        initialMessage: `Merhaba, marketteki "${selectedListing.title}" ilanınız ile ilgileniyorum.`
                                                    }
                                                });
                                            }}
                                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                            {t('marketplace.messageSeller', 'Satıcıya Mesaj At')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Market;

